import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ShoppingBag, 
  Shirt, 
  TrendingUp, 
  ArrowLeft, 
  Plus, 
  Minus, 
  User,
  Heart,
  Bookmark,
  CreditCard,
  Loader2,
  CheckCircle,
  Tag,
  MessageSquare,
  Send
} from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { DualPrice, useExchangeRate } from '@/utils/priceUtils';
import { Lightbulb } from 'lucide-react';
import USDTPaymentDialog from '@/components/USDTPaymentDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomizePage = () => {
  const { artworkId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();

  const [artwork, setArtwork] = useState(null);
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Order form state
  const [productType, setProductType] = useState('tote_bag');
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState('M');
  const [activeTab, setActiveTab] = useState('customize');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Original purchase state
  const [purchasing, setPurchasing] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerPrice, setOfferPrice] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [submittingOffer, setSubmittingOffer] = useState(false);
  const [myOffers, setMyOffers] = useState([]);
  const [showUSDTDialog, setShowUSDTDialog] = useState(false);
  const [usdtAmount, setUsdtAmount] = useState(0);

  // Check for payment success or tab param
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    const tabParam = searchParams.get('tab');
    if (tabParam && ['original', 'customize', 'invest'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/customize/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_status === 'paid') {
        toast.success(t('customize.paymentSuccess', '支付成功！'));
        navigate('/profile/me?tab=customize');
      }
    } catch (err) {
      console.error('Payment check error:', err);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [artworkId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch artwork
        const artworkRes = await axios.get(`${API}/artworks/${artworkId}`);
        setArtwork(artworkRes.data);

        // Set default tab based on artwork properties and URL param
        const tabParam = new URLSearchParams(window.location.search).get('tab');
        if (!tabParam) {
          if (artworkRes.data.is_original_for_sale && artworkRes.data.original_price > 0) {
            setActiveTab('original');
          } else if (artworkRes.data.allow_merchandise) {
            setActiveTab('customize');
          } else {
            setActiveTab('invest');
          }
        }

        // Fetch creator info
        if (artworkRes.data.creator_id) {
          try {
            const creatorRes = await axios.get(`${API}/users/${artworkRes.data.creator_id}/profile`);
            setCreator(creatorRes.data);
          } catch (e) {
            console.log('Creator not found');
          }
        }

        // Fetch products info
        const productsRes = await axios.get(`${API}/customize/products`);
        setProducts(productsRes.data);

        // Fetch payment methods filtered by language
        const payRes = await axios.get(`${API}/payments/methods`, { params: { lang: i18n.language } });
        const methods = payRes.data.methods || [];
        setPaymentMethods(methods);
        const firstEnabled = methods.find(m => m.enabled);
        if (firstEnabled) setPaymentMethod(firstEnabled.id);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        toast.error(t('customize.loadError', '加载失败'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [artworkId, t, i18n.language]);

  // Fetch my offers for this artwork
  useEffect(() => {
    if (!user || !token || !artworkId) return;
    axios.get(`${API}/artwork-purchase/offers/${artworkId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setMyOffers(res.data || []))
      .catch(() => {});
  }, [artworkId, user, token]);

  const getLocalizedField = (item, field) => {
    if (!item) return '';
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  // Calculate price
  const getProductPrice = () => {
    if (!products) return 0;
    return products.products[productType]?.price || 0;
  };

  const getTotalPrice = () => {
    return getProductPrice() * quantity;
  };

  // Handle customize order
  const handleOrder = async () => {
    if (!user) {
      toast.error(t('customize.loginRequired', '请先登录'));
      navigate('/login');
      return;
    }

    // USDT payment: open USDT dialog
    if (paymentMethod === 'usdt') {
      setUsdtAmount(getTotalPrice());
      setShowUSDTDialog(true);
      return;
    }

    setSubmitting(true);
    try {
      // Create order
      const orderRes = await axios.post(`${API}/customize/orders`, {
        artwork_id: artworkId,
        product_type: productType,
        quantity,
        size: productType === 't_shirt' ? size : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (paymentMethod === 'paypal') {
        // PayPal flow
        const paypalRes = await axios.post(`${API}/paypal/create-order`, {
          order_id: orderRes.data.id,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = paypalRes.data.approve_url;
      } else {
        // Stripe flow
        const checkoutRes = await axios.post(`${API}/customize/checkout/order`, {
          order_id: orderRes.data.id,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) {
      console.error('Order error:', err);
      toast.error(err.response?.data?.detail || t('customize.orderError', '下单失败'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle investment
  const handleInvest = async () => {
    if (!user) {
      toast.error(t('customize.loginRequired', '请先登录'));
      navigate('/login');
      return;
    }

    // USDT payment: open USDT dialog
    if (paymentMethod === 'usdt') {
      const investPrice = artwork?.invest_price || artwork?.original_price || 0;
      setUsdtAmount(investPrice);
      setShowUSDTDialog(true);
      return;
    }

    setSubmitting(true);
    try {
      // Create investment
      const investRes = await axios.post(`${API}/customize/investments`, {
        artwork_id: artworkId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (paymentMethod === 'paypal') {
        // PayPal flow
        const paypalRes = await axios.post(`${API}/paypal/create-investment-order`, {
          order_id: investRes.data.id,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = paypalRes.data.approve_url;
      } else {
        // Stripe flow
        const checkoutRes = await axios.post(`${API}/customize/checkout/investment`, {
          order_id: investRes.data.id,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        window.location.href = checkoutRes.data.url;
      }
    } catch (err) {
      console.error('Investment error:', err);
      toast.error(err.response?.data?.detail || t('customize.investError', '投资失败'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle original artwork purchase
  const handlePurchaseOriginal = async () => {
    if (!user) {
      toast.error(t('common.loginRequired'));
      navigate('/login');
      return;
    }
    // USDT payment: open USDT dialog instead of redirect
    if (paymentMethod === 'usdt') {
      const price = artwork?.original_price || artwork?.price || 0;
      setUsdtAmount(price);
      setShowUSDTDialog(true);
      return;
    }
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/artwork-purchase/create`, {
        artwork_id: artworkId,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const checkout = res.data.checkout;
      if (checkout.provider === 'paypal' && checkout.approve_url) {
        window.location.href = checkout.approve_url;
      } else if (checkout.url) {
        window.location.href = checkout.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setPurchasing(false);
    }
  };

  // Submit offer
  const handleSubmitOffer = async () => {
    if (!user) { toast.error(t('common.loginRequired')); navigate('/login'); return; }
    if (!offerPrice || parseFloat(offerPrice) <= 0) { toast.error(t('artwork.invalidPrice')); return; }
    setSubmittingOffer(true);
    try {
      const res = await axios.post(`${API}/artwork-purchase/offer`, {
        artwork_id: artworkId,
        offer_price: parseFloat(offerPrice),
        message: offerMessage || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('artwork.offerSubmitted'));
      setMyOffers(prev => [res.data, ...prev]);
      setShowOfferForm(false);
      setOfferPrice('');
      setOfferMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSubmittingOffer(false);
    }
  };

  // Accept counter offer and pay
  const handleAcceptCounterAndPay = async (offer) => {
    setPurchasing(true);
    try {
      const res = await axios.post(
        `${API}/artwork-purchase/offer/${offer.id}/accept-and-pay`,
        null,
        {
          params: { origin_url: window.location.origin, payment_method: paymentMethod },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const checkout = res.data.checkout;
      if (checkout?.approve_url) window.location.href = checkout.approve_url;
      else if (checkout?.url) window.location.href = checkout.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <p className="text-amber-600 mb-4">{t('customize.artworkNotFound', '作品未找到')}</p>
          <Button onClick={() => navigate('/artworks')}>{t('common.back', '返回')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back', '返回')}
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Artwork preview */}
          <div>
            <Card className="overflow-hidden">
              <div className="aspect-square relative">
                <img loading="lazy"
                  src={getImageUrl(artwork.image_url)}
                  alt={artwork.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <h1 className="text-2xl font-bold mb-2">{getLocalizedField(artwork, 'title')}</h1>
                {artwork.description && (
                  <p className="text-amber-700 mb-4">{getLocalizedField(artwork, 'description')}</p>
                )}
                
                {/* Creator info */}
                {creator && (
                  <div 
                    className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100"
                    onClick={() => navigate(`/profile/${creator.id}`)}
                  >
                    {creator.avatar ? (
                      <img loading="lazy"
                        src={getImageUrl(creator.avatar)} 
                        alt="" 
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-5 h-5 text-amber-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{creator.name}</p>
                      <p className="text-sm text-amber-600">{t('customize.artist', '艺术家')}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Customize options */}
          <div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full mb-6">
                {artwork.is_original_for_sale && artwork.original_price > 0 && (
                  <TabsTrigger value="original" className="flex-1" data-testid="original-purchase-tab">
                    <Tag className="w-4 h-4 mr-2" />
                    {t('customize.originalTab')}
                  </TabsTrigger>
                )}
                {artwork.allow_merchandise && (
                  <TabsTrigger value="customize" className="flex-1" data-testid="customize-tab">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {t('customize.orderTab')}
                  </TabsTrigger>
                )}
                <TabsTrigger value="invest" className="flex-1" data-testid="invest-tab">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t('customize.investTab')}
                </TabsTrigger>
              </TabsList>

              {/* Original Artwork Purchase Tab */}
              {artwork.is_original_for_sale && artwork.original_price > 0 && (
                <TabsContent value="original">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Tag className="w-5 h-5 text-red-500" />
                        {t('customize.originalPurchase')}
                      </CardTitle>
                      <CardDescription>{t('customize.originalPurchaseDesc')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Price with dual display */}
                      <div className="text-center" data-testid="original-price-section">
                        <p className="text-sm text-amber-600 mb-1">{t('artwork.price')}</p>
                        <DualPrice kwhAmount={artwork.original_price} size="xl" className="items-center" />
                        <a href="/why-kwh" className="inline-flex items-center gap-1 text-xs text-amber-500 hover:text-amber-700 mt-2" data-testid="why-kwh-link">
                          <Lightbulb className="w-3 h-3" /> Why kWh?
                        </a>
                      </div>

                      <Separator />

                      {/* Revenue breakdown */}
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-600">{t('artwork.artistReceives')}</p>
                          <p className="text-lg font-bold text-green-700">97%</p>
                        </div>
                        <div className="p-3 bg-amber-50 rounded-lg">
                          <p className="text-sm text-amber-600">{t('artwork.platformFee')}</p>
                          <p className="text-lg font-bold text-amber-700">3%</p>
                          <p className="text-xs text-amber-500">{t('artwork.includesShipping')}</p>
                        </div>
                      </div>

                      {/* Payment Method */}
                      <div>
                        <Label className="text-sm font-medium text-amber-700 mb-2 block">{t('customize.paymentMethod')}</Label>
                        {paymentMethods.length > 0 ? (
                          <RadioGroup value={paymentMethod} onValueChange={(v) => {
                            const method = paymentMethods.find(m => m.id === v);
                            if (method && method.enabled) setPaymentMethod(v);
                          }} className="flex flex-wrap gap-3">
                            {paymentMethods.map(method => (
                              <div key={method.id} className={`flex items-center space-x-2 border rounded-lg px-4 py-2.5 transition-colors ${method.enabled ? 'border-amber-200 cursor-pointer hover:border-orange-400' : 'border-gray-200 opacity-50 cursor-not-allowed'}`}>
                                <RadioGroupItem value={method.id} id={`${method.id}-original`} disabled={!method.enabled} />
                                <Label htmlFor={`${method.id}-original`} className={`flex items-center gap-2 ${method.enabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                  {method.id === 'stripe' && <><CreditCard className="w-4 h-4" /><span className="text-sm">Stripe</span></>}
                                  {method.id === 'paypal' && <><span className="text-sm font-bold text-blue-600">Pay</span><span className="text-sm font-bold text-blue-800">Pal</span></>}
                                  {method.id === 'liqpay' && <span className="text-sm font-bold text-green-600">LiqPay</span>}
                                  {method.id === 'mono' && <span className="text-sm font-bold text-gray-800">mono</span>}
                                  {method.id === 'alipay' && <span className="text-sm font-bold text-blue-500">支付宝 Alipay</span>}
                                  {method.id === 'wechat' && <span className="text-sm font-bold text-green-500">微信支付 WeChat</span>}
                                  {method.id === 'usdt' && <span className="text-sm font-bold text-teal-600">USDT <span className="text-xs font-normal text-teal-400">TRC-20</span></span>}
                                  {method.badge && method.enabled && <Badge className="bg-green-100 text-green-700 text-[10px] ml-1 px-1">{method.badge}</Badge>}
                                  {!method.enabled && <span className="text-xs text-gray-400 ml-1">{t('checkout.comingSoon')}</span>}
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        ) : (
                          <p className="text-sm text-gray-500">{t('checkout.noPaymentMethods')}</p>
                        )}
                      </div>

                      {/* Buy Button */}
                      <Button
                        size="lg"
                        className="w-full bg-red-600 hover:bg-red-700 py-6 text-lg rounded-xl"
                        onClick={handlePurchaseOriginal}
                        disabled={purchasing || !paymentMethod}
                        data-testid="buy-original-btn"
                      >
                        <Tag className="w-5 h-5 mr-2" />
                        {purchasing ? t('common.processing') : t('artwork.buyOriginal')}
                      </Button>

                      <p className="text-xs text-amber-500 text-center">
                        {t('artwork.shippingNote')}
                      </p>

                      <Separator />

                      {/* Offer / Negotiate Section */}
                      <div className="space-y-4" data-testid="offer-section">
                        {!showOfferForm ? (
                          <Button
                            variant="outline"
                            size="lg"
                            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 py-5"
                            onClick={() => setShowOfferForm(true)}
                            data-testid="make-offer-btn"
                          >
                            <MessageSquare className="w-5 h-5 mr-2" />
                            {t('artwork.makeOffer')}
                          </Button>
                        ) : (
                          <div className="space-y-3 p-4 bg-amber-50/50 rounded-xl border border-amber-200">
                            <h4 className="font-medium text-amber-900">{t('artwork.yourOffer')}</h4>
                            <div className="space-y-1">
                              <Label className="text-xs text-amber-600">{t('artwork.offerPrice')}</Label>
                              <Input
                                type="number"
                                value={offerPrice}
                                onChange={(e) => setOfferPrice(e.target.value)}
                                placeholder={`${t('artwork.askingPrice')}: ${artwork.original_price?.toLocaleString()}`}
                                className="border-amber-200"
                                data-testid="offer-price-input"
                              />
                              {offerPrice > 0 && (
                                <p className="text-xs text-amber-500">≈ ${(parseFloat(offerPrice) * 0.1)?.toLocaleString()} USD</p>
                              )}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-amber-600">{t('artwork.offerMessage')}</Label>
                              <Textarea
                                value={offerMessage}
                                onChange={(e) => setOfferMessage(e.target.value)}
                                placeholder={t('artwork.offerMessagePlaceholder')}
                                className="border-amber-200 resize-none h-20"
                                data-testid="offer-message-input"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                className="flex-1 bg-amber-600 hover:bg-amber-700"
                                onClick={handleSubmitOffer}
                                disabled={submittingOffer}
                                data-testid="submit-offer-btn"
                              >
                                <Send className="w-4 h-4 mr-1" />
                                {submittingOffer ? t('common.processing') : t('artwork.submitOffer')}
                              </Button>
                              <Button variant="outline" onClick={() => setShowOfferForm(false)}>
                                {t('common.cancel')}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* My active offers */}
                        {myOffers.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-amber-700">{t('artwork.myOffers')}</h4>
                            {myOffers.map(offer => (
                              <div key={offer.id} className="p-3 border border-amber-200 rounded-lg text-sm" data-testid={`offer-${offer.id}`}>
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">
                                    {offer.offer_price?.toLocaleString()} {t('common.dianUnit')}
                                  </span>
                                  <Badge variant={
                                    offer.status === 'accepted' ? 'default' :
                                    offer.status === 'countered' ? 'secondary' :
                                    offer.status === 'rejected' ? 'destructive' : 'outline'
                                  }>
                                    {{
                                      pending: t('artwork.offerPending'),
                                      accepted: t('artwork.offerAccepted'),
                                      rejected: t('artwork.offerRejected'),
                                      countered: t('artwork.offerCountered'),
                                      withdrawn: t('artwork.offerWithdrawn'),
                                    }[offer.status] || offer.status}
                                  </Badge>
                                </div>
                                {offer.status === 'countered' && offer.counter_price && (
                                  <div className="mt-2 p-2 bg-amber-100 rounded">
                                    <p className="text-amber-800">
                                      {t('artwork.counterPrice')}: <strong>{offer.counter_price?.toLocaleString()} {t('common.dianUnit')}</strong>
                                    </p>
                                    {offer.response_message && (
                                      <p className="text-xs text-amber-600 mt-1">{offer.response_message}</p>
                                    )}
                                    <Button
                                      size="sm"
                                      className="mt-2 bg-green-600 hover:bg-green-700"
                                      onClick={() => handleAcceptCounterAndPay(offer)}
                                      disabled={purchasing}
                                      data-testid="accept-counter-btn"
                                    >
                                      {t('artwork.acceptAndPay')}
                                    </Button>
                                  </div>
                                )}
                                {offer.status === 'accepted' && (
                                  <div className="mt-2">
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => handleAcceptCounterAndPay(offer)}
                                      disabled={purchasing}
                                    >
                                      {t('artwork.proceedToPay')}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              {/* Customize Tab */}
              <TabsContent value="customize">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('customize.selectProduct', '选择产品')}</CardTitle>
                    <CardDescription>
                      {t('customize.selectProductDesc', '将此艺术作品印制在有机棉产品上')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Product Type Selection */}
                    <div>
                      <Label className="mb-3 block">{t('customize.productType', '产品类型')}</Label>
                      <RadioGroup value={productType} onValueChange={setProductType} className="grid grid-cols-2 gap-4">
                        <div>
                          <RadioGroupItem value="tote_bag" id="tote_bag" className="peer sr-only" />
                          <Label
                            htmlFor="tote_bag"
                            className="flex flex-col items-center justify-between rounded-lg border-2 border-gray-200 p-4 hover:bg-amber-50 peer-data-[state=checked]:border-orange-500 cursor-pointer"
                          >
                            <ShoppingBag className="w-8 h-8 mb-2 text-orange-500" />
                            <span className="font-medium">{t('customize.toteBag', '有机棉布包')}</span>
                            <DualPrice kwhAmount={products?.products?.tote_bag?.price || 133} size="sm" />
                          </Label>
                        </div>
                        <div>
                          <RadioGroupItem value="t_shirt" id="t_shirt" className="peer sr-only" />
                          <Label
                            htmlFor="t_shirt"
                            className="flex flex-col items-center justify-between rounded-lg border-2 border-gray-200 p-4 hover:bg-amber-50 peer-data-[state=checked]:border-orange-500 cursor-pointer"
                          >
                            <Shirt className="w-8 h-8 mb-2 text-orange-500" />
                            <span className="font-medium">{t('customize.tShirt', '有机棉T恤')}</span>
                            <DualPrice kwhAmount={products?.products?.t_shirt?.price || 199} size="sm" />
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Size Selection (for T-shirt) */}
                    {productType === 't_shirt' && (
                      <div>
                        <Label className="mb-3 block">{t('customize.size', '尺码')}</Label>
                        <Select value={size} onValueChange={setSize}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map(s => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Quantity */}
                    <div>
                      <Label className="mb-3 block">{t('customize.quantity', '数量')}</Label>
                      <div className="flex items-center gap-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-xl font-bold w-12 text-center">{quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setQuantity(Math.min(100, quantity + 1))}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Price Summary */}
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-amber-700">{t('customize.unitPrice', '单价')}</span>
                        <DualPrice kwhAmount={getProductPrice()} size="sm" />
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-amber-700">{t('customize.quantity', '数量')}</span>
                        <span>× {quantity}</span>
                      </div>
                      <div className="border-t border-orange-200 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold">{t('customize.total', '合计')}</span>
                          <DualPrice kwhAmount={getTotalPrice()} size="lg" />
                        </div>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-amber-700">{t('customize.paymentMethod', '支付方式')}</Label>
                      {paymentMethods.length > 0 ? (
                        <RadioGroup value={paymentMethod} onValueChange={(v) => {
                          const method = paymentMethods.find(m => m.id === v);
                          if (method && method.enabled) setPaymentMethod(v);
                        }} className="flex flex-wrap gap-3">
                          {paymentMethods.map(method => (
                            <div key={method.id} className={`flex items-center space-x-2 border rounded-lg px-4 py-2.5 transition-colors ${method.enabled ? 'border-amber-200 cursor-pointer hover:border-orange-400' : 'border-gray-200 opacity-50 cursor-not-allowed'}`} data-testid={`payment-method-${method.id}`}>
                              <RadioGroupItem value={method.id} id={`${method.id}-order`} disabled={!method.enabled} />
                              <Label htmlFor={`${method.id}-order`} className={`flex items-center gap-2 ${method.enabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                {method.id === 'stripe' && <><CreditCard className="w-4 h-4" /><span className="text-sm">Stripe</span></>}
                                {method.id === 'paypal' && <><span className="text-sm font-bold text-blue-600">Pay</span><span className="text-sm font-bold text-blue-800">Pal</span></>}
                                {method.id === 'liqpay' && <span className="text-sm font-bold text-green-600">LiqPay</span>}
                                {method.id === 'mono' && <span className="text-sm font-bold text-gray-800">mono</span>}
                                {method.id === 'alipay' && <span className="text-sm font-bold text-blue-500">支付宝 Alipay</span>}
                                {method.id === 'wechat' && <span className="text-sm font-bold text-green-500">微信支付 WeChat</span>}
                                {method.id === 'usdt' && <span className="text-sm font-bold text-teal-600">USDT <span className="text-xs font-normal text-teal-400">TRC-20</span></span>}
                                {method.badge && method.enabled && <Badge className="bg-green-100 text-green-700 text-[10px] ml-1 px-1">{method.badge}</Badge>}
                                {!method.enabled && <span className="text-xs text-gray-400 ml-1">{t('checkout.comingSoon', '即将开通')}</span>}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      ) : (
                        <p className="text-sm text-gray-500">{t('checkout.noPaymentMethods', '暂无可用支付方式')}</p>
                      )}
                    </div>

                    {/* Order Button */}
                    <Button 
                      className="w-full bg-orange-500 hover:bg-orange-600 py-6 text-lg"
                      onClick={handleOrder}
                      disabled={submitting || !paymentMethod}
                      data-testid="order-now-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-5 h-5 mr-2" />
                      )}
                      {t('customize.orderNow', '立即定制')}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Invest Tab */}
              <TabsContent value="invest">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-orange-500" />
                      {t('customize.investTitle', '投资产品收益权')}
                    </CardTitle>
                    <CardDescription>
                      {t('customize.investDesc', '投资启动该作品的有机棉产品生产，分享产品销售收益')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Investment Info */}
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl p-6">
                        <div className="text-center mb-4">
                          <DualPrice kwhAmount={products?.investment?.amount || 1000} size="xl" className="items-center text-white [&_span]:text-white [&_span]:text-orange-100" />
                          <div className="text-orange-100 mt-1">{t('customize.investAmount', '投资金额')}</div>
                        </div>
                        <div className="flex justify-center">
                          <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                            {t('customize.revenueShare', '收益分成')} {products?.investment?.revenue_share || 50}%
                          </Badge>
                        </div>
                      </div>

                      {/* Benefits */}
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                          <div>
                            <p className="font-medium">{t('customize.benefit1Title', '启动生产')}</p>
                            <p className="text-sm text-amber-600">{t('customize.benefit1Desc', '您的投资将用于启动该作品的有机棉产品生产')}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                          <div>
                            <p className="font-medium">{t('customize.benefit2Title', '持续收益')}</p>
                            <p className="text-sm text-amber-600">{t('customize.benefit2Desc', '每售出一件衍生品，投资人获得销售额50%，艺术家获得10%')}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                          <div>
                            <p className="font-medium">{t('customize.benefit3Title', '支持艺术家')}</p>
                            <p className="text-sm text-amber-600">{t('customize.benefit3Desc', '帮助艺术家将创意变为实物产品，共同创造价值')}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-amber-700">{t('customize.paymentMethod', '支付方式')}</Label>
                      {paymentMethods.length > 0 ? (
                        <RadioGroup value={paymentMethod} onValueChange={(v) => {
                          const method = paymentMethods.find(m => m.id === v);
                          if (method && method.enabled) setPaymentMethod(v);
                        }} className="flex flex-wrap gap-3">
                          {paymentMethods.map(method => (
                            <div key={method.id} className={`flex items-center space-x-2 border rounded-lg px-4 py-2.5 transition-colors ${method.enabled ? 'border-amber-200 cursor-pointer hover:border-orange-400' : 'border-gray-200 opacity-50 cursor-not-allowed'}`} data-testid={`invest-payment-${method.id}`}>
                              <RadioGroupItem value={method.id} id={`${method.id}-invest`} disabled={!method.enabled} />
                              <Label htmlFor={`${method.id}-invest`} className={`flex items-center gap-2 ${method.enabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                {method.id === 'stripe' && <><CreditCard className="w-4 h-4" /><span className="text-sm">Stripe</span></>}
                                {method.id === 'paypal' && <><span className="text-sm font-bold text-blue-600">Pay</span><span className="text-sm font-bold text-blue-800">Pal</span></>}
                                {method.id === 'liqpay' && <span className="text-sm font-bold text-green-600">LiqPay</span>}
                                {method.id === 'mono' && <span className="text-sm font-bold text-gray-800">mono</span>}
                                {method.id === 'alipay' && <span className="text-sm font-bold text-blue-500">支付宝 Alipay</span>}
                                {method.id === 'wechat' && <span className="text-sm font-bold text-green-500">微信支付 WeChat</span>}
                                {method.id === 'usdt' && <span className="text-sm font-bold text-teal-600">USDT <span className="text-xs font-normal text-teal-400">TRC-20</span></span>}
                                {method.badge && method.enabled && <Badge className="bg-green-100 text-green-700 text-[10px] ml-1 px-1">{method.badge}</Badge>}
                                {!method.enabled && <span className="text-xs text-gray-400 ml-1">{t('checkout.comingSoon', '即将开通')}</span>}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      ) : (
                        <p className="text-sm text-gray-500">{t('checkout.noPaymentMethods', '暂无可用支付方式')}</p>
                      )}
                    </div>

                    {/* Invest Button */}
                    <Button 
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 py-6 text-lg"
                      onClick={handleInvest}
                      disabled={submitting || !paymentMethod}
                      data-testid="invest-now-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <TrendingUp className="w-5 h-5 mr-2" />
                      )}
                      {t('customize.investNow', '立即投资')}
                    </Button>

                    <p className="text-xs text-amber-500 text-center">
                      {t('customize.investDisclaimer', '投资有风险，收益取决于产品销售情况')}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <USDTPaymentDialog
        open={showUSDTDialog}
        onOpenChange={setShowUSDTDialog}
        artworkId={artworkId}
        amountUsd={usdtAmount}
        artworkTitle={artwork?.title || ''}
        onSuccess={() => {
          setShowUSDTDialog(false);
          toast.success(t('checkout.paymentSuccess', 'Payment confirmed!'));
          navigate('/profile');
        }}
      />
    </div>
  );
};

export default CustomizePage;
